import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';

import { Loading } from '../../../components';
import { MASection } from '../MasterAdminSettings';
import { StorePanel } from './StorePanel';
import { StoreDraft } from './StoreDraft';
import { ProductUpdatesPanel } from './ProductUpdatesPanel';
import { planLabel } from '../../planVocab';

import './stores-console.scss';

/**
 * STORES — one section for every question a master admin has about a store.
 *
 * Replaces three sections that each asked "which store?" and answered it
 * differently: the Stores console, Plans & Add-ons, and Merchant Chat Groups.
 * There is now one search, one list, one detail panel, and no overlays.
 *
 * Design: the "Stores + Plans merged" canvas (2026-10-27).
 */

const IStore = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M3 9.5 4.6 4.8A1.5 1.5 0 0 1 6 3.8h12a1.5 1.5 0 0 1 1.4 1L21 9.5" />
    <path d="M4 9.5h16V19a1.5 1.5 0 0 1-1.5 1.5h-13A1.5 1.5 0 0 1 4 19z" />
    <path d="M9 20.5V14h6v6.5" />
  </svg>
);

const IPlus = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true">
    <path d="M12 5v14M5 12h14" />
  </svg>
);

const IMegaphone = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M4 11.5 19 5v14L4 12.5z" />
    <path d="M8 13v4a2 2 0 0 0 4 0v-2.5" />
  </svg>
);

const CHIPS = [
  { key: 'all', label: 'All' },
  { key: 'subscribed', label: 'Paying' },
  // Beta = a plan nobody is paying for (a trial, or comped). A store with no
  // plan at all is its own category: it used to be lumped in with beta, which
  // made one count mean two things.
  { key: 'beta', label: 'Beta' },
  { key: 'no_plan', label: 'No plan' },
  { key: 'no_chat', label: 'No chat group' },
];

const CHAT_WORDS = {
  live: 'chat live',
  hidden: 'chat hidden',
  error: 'chat needs attention',
  none: 'no chat group',
};

/** One line under a store's name, so the row answers three questions at once. */
const rowMeta = (store) => {
  const seats = store.seats > 0
    ? `${store.seats} on the team`
    : 'nobody yet';

  return `#${store.id} · ${seats} · ${CHAT_WORDS[store.chat] || CHAT_WORDS.none}`;
};

const statePill = (store) => {
  if (store.suspended) return { className: 'rc-sc__pill rc-sc__pill--bad', text: 'SUSPENDED' };
  if (store.state === 'none') return { className: 'rc-sc__pill', text: 'NO PLAN' };
  if (store.state === 'lapsed') return { className: 'rc-sc__pill rc-sc__pill--bad', text: 'LAPSED' };
  if (store.state === 'beta') return { className: 'rc-sc__pill rc-sc__pill--beta', text: 'BETA' };
  return { className: 'rc-sc__pill rc-sc__pill--plan', text: planLabel(store.plan) || 'PLAN' };
};

export function StoresConsoleSection() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [q, setQ] = useState('');
  const [filter, setFilter] = useState('all');
  const [selected, setSelected] = useState(null);
  const [view, setView] = useState('store'); // store | updates | draft
  const [notice, setNotice] = useState('');

  // Two requests can be in flight at once (typing while a chip changes), and
  // the slower one must not win. Shipped once without this and the list
  // ignored what you had typed.
  const seq = useRef(0);

  const load = useCallback(async (query, chip) => {
    const mine = ++seq.current;
    setLoading(true);
    setError('');
    try {
      const { data: payload } = await axios.get('/api/admin/stores/console', {
        params: { ...(query ? { q: query } : {}), filter: chip },
      });
      if (mine !== seq.current) return;
      setData(payload);
    } catch (e) {
      if (mine !== seq.current) return;
      setError(e?.response?.data?.message || 'Could not load the stores.');
    } finally {
      if (mine === seq.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => load(q.trim(), filter), 300);
    return () => clearTimeout(timer);
  }, [q, filter, load]);

  const refresh = useCallback(() => load(q.trim(), filter), [load, q, filter]);

  const counts = data?.counts || {};
  const items = data?.items || [];

  const openStore = (store) => { setSelected(store); setView('store'); setNotice(''); };

  return (
    <MASection
      title="Stores"
      icon={<IStore />}
      anchor="stores"
      headerExtra={data ? (
        <span className="ma-section__count">
          {data.total} stores · {counts.subscribed ?? 0} on a plan
        </span>
      ) : null}
    >
      <div className="rc-sc">
        {/* Section-level actions sit in the section's own toolbar. Product
            Updates spent a day inside the store LIST, which implied it was a
            store — it is the one channel that belongs to none (Alex,
            2026-10-27). New store is not a store either until it is saved. */}
        <div className="rc-sc__toolbar">
          <p className="rc-sc__intro">
            Everything about a store in one place — who is on it, what it pays for, and whether it
            has a chat group.
          </p>
          <span className="rc-sc__spacer" />
          <button
            type="button"
            aria-pressed={view === 'updates'}
            className={`rc-sc__btn ${view === 'updates' ? 'rc-sc__btn--on' : ''}`}
            onClick={() => { setView('updates'); setSelected(null); setNotice(''); }}
          >
            <IMegaphone />
            Product Updates
          </button>
          <button
            type="button"
            className="rc-sc__btn"
            onClick={() => { setView('draft'); setSelected(null); setNotice(''); }}
          >
            <IPlus />
            New store
          </button>
        </div>

        {notice && <p className="rc-sc__hint" role="status">{notice}</p>}
        {error && <p className="rc-sc__error" role="alert">{error}</p>}

        <div className="rc-sc__cols">
          <aside className="rc-sc__list">
            <label style={{ display: 'block' }}>
              <span className="rc-sc__search-label">Find a store</span>
              <input
                className="rc-sc__input"
                type="search"
                value={q}
                placeholder="Name or store id"
                onChange={(e) => setQ(e.target.value)}
              />
            </label>

            <div className="rc-sc__chips" role="group" aria-label="Filter stores">
              {CHIPS.map((chip) => (
                <button
                  key={chip.key}
                  type="button"
                  aria-pressed={filter === chip.key}
                  className={`rc-sc__chip ${filter === chip.key ? 'rc-sc__chip--on' : ''}`}
                  onClick={() => setFilter(chip.key)}
                >
                  {chip.label}
                  {counts[chip.key] !== undefined && <span>{counts[chip.key]}</span>}
                </button>
              ))}
            </div>

            <div className="rc-sc__rows">
              {loading && !data ? <Loading /> : (
                <>
                  {items.length === 0 && <p className="rc-sc__empty">No store matches that.</p>}

                  {items.map((store) => {
                    const pill = statePill(store);
                    const on = view === 'store' && selected?.id === store.id;

                    return (
                      <button
                        type="button"
                        key={store.id}
                        aria-current={on || undefined}
                        className={`rc-sc__row ${on ? 'rc-sc__row--on' : ''}`}
                        onClick={() => openStore(store)}
                      >
                        <span className="rc-sc__row-body">
                          <span className="rc-sc__row-name">{store.name || `Store ${store.id}`}</span>
                          <span className="rc-sc__row-meta">{rowMeta(store)}</span>
                        </span>
                        <span className={pill.className}>{pill.text}</span>
                      </button>
                    );
                  })}
                </>
              )}
            </div>

            <p className="rc-sc__foot">
              Showing {items.length} of {data?.total ?? 0}
            </p>
          </aside>

          {view === 'draft' && (
            <StoreDraft
              onCancel={() => setView('store')}
              onCreated={(store, warning) => {
                setNotice(warning || `${store?.name || 'The store'} was created.`);
                refresh();
                if (store?.id) openStore({ id: store.id, name: store.name, chat: 'none', seats: 0 });
              }}
            />
          )}

          {view === 'updates' && <ProductUpdatesPanel onOpenStore={openStore} />}

          {view === 'store' && selected && (
            <StorePanel key={selected.id} store={selected} onChanged={refresh} />
          )}

          {view === 'store' && !selected && (
            <section className="rc-sc__panel">
              <p className="rc-sc__hint">Pick a store on the left, or create one.</p>
            </section>
          )}
        </div>
      </div>
    </MASection>
  );
}

export default StoresConsoleSection;
